import React, { useMemo, useState } from "react";
import { Button, Input, Popover } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./ClockTimePicker.css";

const DIAL_SIZE = 248;
const CENTER = DIAL_SIZE / 2;
const OUTER_RADIUS = 100;
const INNER_RADIUS = 66;
const pad = (n) => String(n).padStart(2, "0");

const polar = (index, radius) => {
  const angle = (index / 12) * 2 * Math.PI;
  return {
    left: CENTER + radius * Math.sin(angle),
    top: CENTER - radius * Math.cos(angle),
  };
};

/**
 * 24-hour clock-face time picker (hour dial, then minute dial).
 * `disabledTime` uses the same shape as antd TimePicker:
 * () => ({ disabledHours?: () => number[], disabledMinutes?: (hour) => number[] })
 * `durationFrom` (optional dayjs) shows the total time from it to the selection.
 */
const ClockTimePicker = ({
  value,
  onChange,
  disabledTime,
  durationFrom,
  placeholder = "Select time",
  size = "large",
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("hour");
  const [draftHour, setDraftHour] = useState(null);
  const [draftMinute, setDraftMinute] = useState(null);

  const rules = disabledTime?.() || {};
  const disabledHours = new Set(rules.disabledHours?.() || []);
  const disabledMinutesFor = (hour) =>
    new Set(rules.disabledMinutes?.(hour) || []);

  const isHourAllowed = (hour) =>
    !disabledHours.has(hour) && disabledMinutesFor(hour).size < 60;
  const isMinuteAllowed = (hour, minute) =>
    hour !== null &&
    minute !== null &&
    isHourAllowed(hour) &&
    !disabledMinutesFor(hour).has(minute);
  const firstAllowedMinute = (hour) => {
    for (let m = 0; m < 60; m += 1) {
      if (isMinuteAllowed(hour, m)) return m;
    }
    return null;
  };

  const handleOpenChange = (next) => {
    if (next) {
      const current = value ? dayjs(value) : null;
      setDraftHour(current ? current.hour() : null);
      setDraftMinute(current ? current.minute() : null);
      setMode("hour");
    }
    setOpen(next);
  };

  const handleOk = () => {
    if (!isMinuteAllowed(draftHour, draftMinute)) return;
    const base = value ? dayjs(value) : dayjs();
    onChange?.(
      base.hour(draftHour).minute(draftMinute).second(0).millisecond(0)
    );
    setOpen(false);
  };

  const selectHour = (hour) => {
    if (!isHourAllowed(hour)) return;
    setDraftHour(hour);
    if (draftMinute === null || !isMinuteAllowed(hour, draftMinute)) {
      setDraftMinute(firstAllowedMinute(hour));
    }
    setMode("minute");
  };

  const selectMinute = (minute) => {
    if (!isMinuteAllowed(draftHour, minute)) return;
    setDraftMinute(minute);
  };

  const hourItems = useMemo(
    () => [
      ...Array.from({ length: 12 }, (_, i) => {
        const hour = i === 0 ? 0 : i + 12;
        return { hour, label: pad(hour), ...polar(i, OUTER_RADIUS) };
      }),
      ...Array.from({ length: 12 }, (_, i) => {
        const hour = i === 0 ? 12 : i;
        return {
          hour,
          label: pad(hour),
          inner: true,
          ...polar(i, INNER_RADIUS),
        };
      }),
    ],
    []
  );

  const minuteItems = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        minute: i * 5,
        label: pad(i * 5),
        ...polar(i, OUTER_RADIUS),
      })),
    []
  );

  const handleMinuteDialClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - CENTER;
    const y = e.clientY - rect.top - CENTER;
    if (Math.hypot(x, y) < 24) return;
    let angle = Math.atan2(x, -y) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    selectMinute(Math.round(angle / 6) % 60);
  };

  const handPosition = (() => {
    if (mode === "hour" && draftHour !== null) {
      const item = hourItems.find((h) => h.hour === draftHour);
      return item ? { left: item.left, top: item.top } : null;
    }
    if (mode === "minute" && draftMinute !== null) {
      const angle = (draftMinute / 60) * 2 * Math.PI;
      return {
        left: CENTER + OUTER_RADIUS * Math.sin(angle),
        top: CENTER - OUTER_RADIUS * Math.cos(angle),
      };
    }
    return null;
  })();

  const totalTime = (() => {
    if (!durationFrom || draftHour === null || draftMinute === null) {
      return null;
    }
    const from = dayjs(durationFrom);
    const minutes =
      draftHour * 60 + draftMinute - (from.hour() * 60 + from.minute());
    if (minutes <= 0) return null;
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
  })();

  const inputValue = value ? dayjs(value).format("HH:mm") : "";

  const content = (
    <div className="clock-picker">
      <div className="clock-picker-hint">
        {mode === "hour" ? (
          "Select hour · highlighted times are available"
        ) : (
          <>
            Select minutes ·{" "}
            <button
              type="button"
              className="clock-link-btn"
              onClick={() => setMode("hour")}
            >
              Change hour
            </button>
          </>
        )}
      </div>

      {totalTime && (
        <div className="clock-picker-total">
          <span className="clock-picker-total-label">Total time</span>
          <span className="clock-picker-total-value">{totalTime}</span>
        </div>
      )}

      <div
        className={`clock-dial ${mode === "minute" ? "minute-mode" : ""}`}
        style={{ width: DIAL_SIZE, height: DIAL_SIZE }}
        onClick={mode === "minute" ? handleMinuteDialClick : undefined}
      >
        <svg className="clock-hand-layer" width={DIAL_SIZE} height={DIAL_SIZE}>
          {mode === "minute" &&
            Array.from({ length: 60 }, (_, m) => {
              if (m % 5 === 0) return null;
              const angle = (m / 60) * 2 * Math.PI;
              const r = OUTER_RADIUS;
              return (
                <circle
                  key={m}
                  cx={CENTER + r * Math.sin(angle)}
                  cy={CENTER - r * Math.cos(angle)}
                  r={2.5}
                  className={
                    isMinuteAllowed(draftHour, m)
                      ? "clock-tick allowed"
                      : "clock-tick"
                  }
                />
              );
            })}
          {handPosition && (
            <line
              x1={CENTER}
              y1={CENTER}
              x2={handPosition.left}
              y2={handPosition.top}
              className="clock-hand"
            />
          )}
          <circle cx={CENTER} cy={CENTER} r={4} className="clock-center" />
        </svg>

        {mode === "hour"
          ? hourItems.map((item) => {
              const allowed = isHourAllowed(item.hour);
              const selected = draftHour === item.hour;
              return (
                <button
                  key={`h-${item.hour}`}
                  type="button"
                  className={`clock-number ${item.inner ? "inner" : ""} ${
                    allowed ? "allowed" : "blocked"
                  } ${selected ? "selected" : ""}`}
                  style={{ left: item.left, top: item.top }}
                  disabled={!allowed}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectHour(item.hour);
                  }}
                >
                  {item.label}
                </button>
              );
            })
          : minuteItems.map((item) => {
              const allowed = isMinuteAllowed(draftHour, item.minute);
              const selected = draftMinute === item.minute;
              return (
                <button
                  key={`m-${item.minute}`}
                  type="button"
                  className={`clock-number ${allowed ? "allowed" : "blocked"} ${
                    selected ? "selected" : ""
                  }`}
                  style={{ left: item.left, top: item.top }}
                  disabled={!allowed}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectMinute(item.minute);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
      </div>

      <div className="clock-picker-footer">
        <Button size="small" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!isMinuteAllowed(draftHour, draftMinute)}
          onClick={handleOk}
        >
          OK
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <Input
        size={size}
        readOnly
        value={inputValue}
        placeholder={placeholder}
        suffix={<ClockCircleOutlined style={{ color: "rgba(0,0,0,0.25)" }} />}
        style={{ cursor: "pointer", ...style }}
      />
    </Popover>
  );
};

export default ClockTimePicker;
